/* eslint-disable max-lines-per-function */
import React from "react";

import { Link, RouteComponentProps, useParams } from "@reach/router";
import { useMutation, useQuery } from "@apollo/client";

import { Button, ButtonProps, CircularProgress, Grid } from "@material-ui/core";
import logger from "services/logger";

import { useSnackbar } from "components/haven-design/snackbar";
import {
  AdminArchiveAccountMutation,
  AdminArchiveAccountMutationVariables,
  AdminDetailedListUsersQuery,
  AdminDetailedListUsersQueryVariables,
  AdminUpdateAccountMutation,
  AdminUpdateAccountMutationVariables,
  AdminUserProfileInput,
  PostgresUserRole,
} from "types/gql-types";
import Form, { FormProps } from "./form";
import InterestsList from "./components/interest-list";

import { ArchiveAccountMutation, DetailedListUsersQuery, UpdateAccountMutation } from "./queries";
import { ErrorCodes } from "./new";

const confirmPrompt = `
Are you sure you want to archive this user?

This will free their login email and phone
number to be used by other accounts but will
lock them out from being able to login.

Please remember to transfer all of the users
interests to a new user when applicable.
`;

const ApplicantDetails: React.FC<RouteComponentProps> = () => {
  const { openSnackbar } = useSnackbar();
  const { id } = useParams();

  const { data, loading, error } = useQuery<
  AdminDetailedListUsersQuery,
  AdminDetailedListUsersQueryVariables
  >(DetailedListUsersQuery, { variables: { id } });

  const [updateUser] = useMutation<
  AdminUpdateAccountMutation,
  AdminUpdateAccountMutationVariables
  >(UpdateAccountMutation, {
    refetchQueries: ["AdminListUsersQuery"],
  });

  const [archiveUser] = useMutation<
  AdminArchiveAccountMutation,
  AdminArchiveAccountMutationVariables
  >(ArchiveAccountMutation, {
    refetchQueries: ["AdminListUsersQuery"],
  });

  const [user] = data?.adminApplicants || [];

  if (loading) {
    return (
      <Grid container item justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (error || !user) return <p>Error loading applicant account.</p>;

  const handleUpdateUser: FormProps["onSubmit"] = async (variables, formikHelpers) => {
    try {
      const roles = variables.roles as PostgresUserRole[];
      const profile = variables.profile as AdminUserProfileInput;
      await updateUser({ variables: { id: user.id,
        ...variables,
        roles,
        profile: {
          ...profile,
          firstName: profile.firstName && profile.firstName.trim(),
          lastName: profile.lastName && profile.lastName.trim(),
        } } });

      openSnackbar({ message: "Updated Account Successfully", status: "success" });
    } catch (e) {
      logger.sendError(e as Error);
      openSnackbar({ message: "Updating Account Failed", status: "error" });
      switch ((e as Error).message) {
        case ErrorCodes.DuplicateEmailAccount:
          formikHelpers.setFieldError("user.loginEmail", "Already exists");
          break;
        case ErrorCodes.DuplicatePhoneAccount:
          formikHelpers.setFieldError("user.loginPhone", "Already exists");
          break;
        case ErrorCodes.InvalidPassword:
          formikHelpers.setFieldError("user.password", "Incorrect password");
          break;

        default:
          console.error(e);
          break;
      }
    }
  };

  const handleArchiveUser: ButtonProps["onClick"] = async (event) => {
    event.preventDefault();

    // eslint-disable-next-line no-alert
    if (!confirm(confirmPrompt)) return;

    try {
      await archiveUser({ variables: { id } });

      openSnackbar({ message: "Archived Account Successfully", status: "success" });
    } catch (e) {
      console.error(e);
      openSnackbar({ message: "Archiving Account Failed", status: "error" });
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <Grid container spacing={2}>
        <Grid xs={12} container item justifyContent="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleArchiveUser}
          >
            Archive
          </Button>
        </Grid>

        <Form user={user} onSubmit={handleUpdateUser} />

        <Grid item xs={12}>
          <dl>
            <dt>ID:</dt>
            <dd>{user.id}</dd>

            {user.organization && (
              <>
                <dt>Organization:</dt>
                <dd>
                  <Link to={`/dash/admin/organizations/${user.organization.id}`}>
                    {user.organization.name}
                  </Link>
                </dd>
              </>
            )}

            <dt># of Interests:</dt>
            <dd>{user.interests?.length || 0}</dd>
          </dl>
        </Grid>

        <Grid item xs={12}>
          <hr />

          <h3>Interests</h3>
          <InterestsList interests={user.interests} />
        </Grid>
      </Grid>
    </div>
  );
};

export default ApplicantDetails;
